/**
 * Attachment Service
 * Persists trade journal screenshots via the backend API.
 * Pending (pre-create) trades still stage in localStorage until a real trade UUID exists.
 */

import { Attachment, AttachmentUploadResult } from "../types/attachment";
import { BACKEND_API_URL, getClerkToken } from "./apiClient";
import { ApiRequestError, parseJsonOrThrow } from "../utils/apiRequestError";
import { parseApiError } from "../utils/parseApiError";

const STORAGE_KEY_PREFIX = "tradeLog_attachments_";
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isPersistedTradeId = (tradeId: string): boolean =>
  Boolean(tradeId && UUID_REGEX.test(tradeId.trim()));

function throwIfNoAuthToken(): never {
  throw new ApiRequestError("Authentication required. Please sign in.", 401, {
    slug: "auth.required",
    message: "Authentication required. Please sign in.",
  });
}

async function throwAttachmentNotOk(response: Response, fallback: string): Promise<never> {
  if (response.status === 401) {
    throw new ApiRequestError("Session expired. Please log in again.", 401, {
      slug: "auth.token_expired",
      message: "Session expired. Please log in again.",
    });
  }
  const parsed = await parseApiError(response);
  throw new ApiRequestError(parsed.message?.trim() || fallback, response.status, parsed);
}

const getStorageKey = (tradeId: string): string => `${STORAGE_KEY_PREFIX}${tradeId}`;

const mapApiAttachment = (raw: Record<string, unknown>): Attachment => ({
  id: String(raw.id),
  tradeId: String(raw.tradeId ?? raw.trade_id),
  fileName: String(raw.fileName ?? raw.file_name ?? "screenshot"),
  fileType: String(raw.fileType ?? raw.file_type ?? "image/png"),
  fileSize: Number(raw.fileSize ?? raw.file_size ?? 0),
  fileData: String(raw.fileData ?? raw.file_data ?? ""),
  thumbnailData:
    raw.thumbnailData != null || raw.thumbnail_data != null
      ? String(raw.thumbnailData ?? raw.thumbnail_data)
      : undefined,
  uploadedAt: new Date(String(raw.uploadedAt ?? raw.uploaded_at ?? Date.now())),
  userId: String(raw.userId ?? raw.user_id ?? ""),
});

const loadLocalAttachments = (tradeId: string): Attachment[] => {
  try {
    const stored = localStorage.getItem(getStorageKey(tradeId));
    if (!stored) return [];
    const attachments = JSON.parse(stored) as Attachment[];
    return attachments.map((attachment) => ({
      ...attachment,
      uploadedAt: new Date(attachment.uploadedAt),
    }));
  } catch (error) {
    console.error("Error loading local attachments:", error);
    return [];
  }
};

const saveLocalAttachments = (tradeId: string, attachments: Attachment[]): void => {
  try {
    localStorage.setItem(getStorageKey(tradeId), JSON.stringify(attachments));
  } catch (error) {
    console.error("Error saving local attachments:", error);
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      throw new Error("Storage quota exceeded. Please delete some attachments.");
    }
    throw new Error("Failed to save attachment");
  }
};

const fetchApiAttachments = async (tradeId: string): Promise<Attachment[]> => {
  const token = await getClerkToken();
  if (!token) {
    throwIfNoAuthToken();
  }

  const response = await fetch(`${BACKEND_API_URL}/trades/${tradeId}/attachments`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    await throwAttachmentNotOk(response, `Failed to load attachments (${response.status})`);
  }

  const payload = await parseJsonOrThrow<{ attachments?: Record<string, unknown>[] }>(
    response,
    `Failed to load attachments (${response.status})`
  );
  return (payload.attachments ?? []).map((row) => mapApiAttachment(row));
};

const uploadApiAttachment = async (
  file: File,
  tradeId: string,
  userId: string,
  thumbnailBase64?: string
): Promise<Attachment> => {
  const token = await getClerkToken();
  if (!token) {
    throwIfNoAuthToken();
  }

  const formData = new FormData();
  formData.append("file", file);
  if (thumbnailBase64) {
    formData.append("thumbnail_base64", thumbnailBase64);
  }

  const response = await fetch(`${BACKEND_API_URL}/trades/${tradeId}/attachments`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    await throwAttachmentNotOk(response, `Failed to upload attachment (${response.status})`);
  }

  const payload = await parseJsonOrThrow<Record<string, unknown>>(
    response,
    `Failed to upload attachment (${response.status})`
  );
  const attachment = mapApiAttachment(payload);
  return { ...attachment, userId: attachment.userId || userId };
};

const deleteApiAttachment = async (tradeId: string, attachmentId: string): Promise<boolean> => {
  const token = await getClerkToken();
  if (!token) {
    throwIfNoAuthToken();
  }

  const response = await fetch(
    `${BACKEND_API_URL}/trades/${tradeId}/attachments/${attachmentId}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 404) return false;
  if (!response.ok) {
    await throwAttachmentNotOk(response, `Failed to delete attachment (${response.status})`);
  }
  return true;
};

/**
 * Get all attachments for a trade.
 */
export const getAttachments = async (tradeId: string): Promise<Attachment[]> => {
  if (!tradeId) return [];
  if (!isPersistedTradeId(tradeId)) {
    return loadLocalAttachments(tradeId);
  }
  return fetchApiAttachments(tradeId);
};

/**
 * Get a single attachment by ID.
 */
export const getAttachment = async (
  tradeId: string,
  attachmentId: string
): Promise<Attachment | null> => {
  const attachments = await getAttachments(tradeId);
  return attachments.find((attachment) => attachment.id === attachmentId) || null;
};

/**
 * Save an attachment locally (pending trades only).
 */
export const saveAttachment = (attachment: Attachment): Attachment => {
  const attachments = loadLocalAttachments(attachment.tradeId);
  attachments.push(attachment);
  saveLocalAttachments(attachment.tradeId, attachments);
  return attachment;
};

/**
 * Delete an attachment.
 */
export const deleteAttachment = async (
  tradeId: string,
  attachmentId: string
): Promise<boolean> => {
  if (!isPersistedTradeId(tradeId)) {
    const attachments = loadLocalAttachments(tradeId);
    const filtered = attachments.filter((attachment) => attachment.id !== attachmentId);
    if (filtered.length === attachments.length) return false;
    saveLocalAttachments(tradeId, filtered);
    return true;
  }
  return deleteApiAttachment(tradeId, attachmentId);
};

/**
 * Delete all attachments for a trade.
 */
export const deleteAttachmentsForTrade = async (tradeId: string): Promise<void> => {
  if (!tradeId) return;
  if (!isPersistedTradeId(tradeId)) {
    localStorage.removeItem(getStorageKey(tradeId));
    return;
  }
  const attachments = await fetchApiAttachments(tradeId);
  await Promise.all(attachments.map((attachment) => deleteApiAttachment(tradeId, attachment.id)));
};

/**
 * Move staged attachments from a temporary id to the persisted trade id after create.
 */
export const migrateAttachmentsTradeId = async (
  fromTradeId: string,
  toTradeId: string
): Promise<void> => {
  if (!fromTradeId || !toTradeId || fromTradeId === toTradeId) return;
  const attachments = loadLocalAttachments(fromTradeId);
  localStorage.removeItem(getStorageKey(fromTradeId));
  if (attachments.length === 0 || !isPersistedTradeId(toTradeId)) return;

  const attachment = attachments[0];
  const blob = await fetch(
    `data:${attachment.fileType};base64,${attachment.fileData}`
  ).then((response) => response.blob());
  const file = new File([blob], attachment.fileName, { type: attachment.fileType });
  await uploadApiAttachment(file, toTradeId, attachment.userId, attachment.thumbnailData);
};

/**
 * Restore attachments from a saved journal snapshot (discard flow).
 */
export const restoreAttachments = async (
  tradeId: string,
  attachments: Attachment[]
): Promise<void> => {
  if (!tradeId) return;
  if (!isPersistedTradeId(tradeId)) {
    saveLocalAttachments(tradeId, attachments.map((attachment) => ({ ...attachment })));
    return;
  }

  const current = await fetchApiAttachments(tradeId);
  await Promise.all(
    current.map((attachment) => deleteApiAttachment(tradeId, attachment.id))
  );

  const snapshot = attachments[0];
  if (!snapshot?.fileData) return;

  const blob = await fetch(
    `data:${snapshot.fileType};base64,${snapshot.fileData}`
  ).then((response) => response.blob());
  const file = new File([blob], snapshot.fileName, { type: snapshot.fileType });
  await uploadApiAttachment(file, tradeId, snapshot.userId, snapshot.thumbnailData);
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1] || result;
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

export const createThumbnail = (
  file: File,
  maxWidth: number = 200,
  maxHeight: number = 200
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        const thumbnail = canvas.toDataURL("image/jpeg", 0.7);
        const base64 = thumbnail.split(",")[1] || thumbnail;
        resolve(base64);
      };
      img.onerror = (error) => reject(error);
      img.src = e.target?.result as string;
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Upload a file and create an attachment.
 */
export const uploadAttachment = async (
  file: File,
  tradeId: string,
  userId: string
): Promise<AttachmentUploadResult> => {
  try {
    if (!file.type.startsWith("image/")) {
      return {
        attachment: {} as Attachment,
        success: false,
        error: "Only image files are supported",
      };
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        attachment: {} as Attachment,
        success: false,
        error: "File size must be less than 5MB",
      };
    }

    let thumbnailData: string | undefined;
    try {
      thumbnailData = await createThumbnail(file);
    } catch (error) {
      console.warn("Failed to create thumbnail:", error);
    }

    if (isPersistedTradeId(tradeId)) {
      const attachment = await uploadApiAttachment(file, tradeId, userId, thumbnailData);
      return { attachment, success: true };
    }

    const fileData = await fileToBase64(file);
    const attachment: Attachment = {
      id: `attachment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      tradeId,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      fileData,
      thumbnailData,
      uploadedAt: new Date(),
      userId,
    };
    saveAttachment(attachment);
    return { attachment, success: true };
  } catch (error) {
    console.error("Error uploading attachment:", error);
    return {
      attachment: {} as Attachment,
      success: false,
      error: error instanceof Error ? error.message : "Failed to upload attachment",
    };
  }
};

export const getAttachmentDataURL = (attachment: Attachment): string => {
  if (attachment.fileData.startsWith("data:")) {
    return attachment.fileData;
  }
  return `data:${attachment.fileType};base64,${attachment.fileData}`;
};

export const getThumbnailDataURL = (attachment: Attachment): string | null => {
  if (!attachment.thumbnailData) {
    return null;
  }
  if (attachment.thumbnailData.startsWith("data:")) {
    return attachment.thumbnailData;
  }
  return `data:image/jpeg;base64,${attachment.thumbnailData}`;
};
